import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { API_URL } from 'config/env';
import storage from 'utils/storage';

/**
 * Subscribe to server-sent board events (issue created/updated/deleted/status)
 * and invalidate issues/project queries so the UI updates in real time.
 */
export function useBoardEvents(projectId?: string) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const token = storage.getToken();
    if (!token) return undefined;

    const params = new URLSearchParams({ token });
    if (projectId) params.set('projectId', projectId);
    const url = `${API_URL}/events?${params.toString()}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event: MessageEvent) => {
      try {
        const raw = typeof event.data === 'string' ? event.data : String(event.data);
        const data = JSON.parse(raw) as {
          type: string;
          projectId?: string;
          issueId?: string;
        };
        if (data.type?.startsWith('issue.')) {
          queryClient.invalidateQueries({ queryKey: ['issues'] }).catch(() => {});
          const pid = data.projectId;
          if (pid && typeof pid === 'string') {
            queryClient.invalidateQueries({ queryKey: ['issues', pid] }).catch(() => {});
            queryClient.invalidateQueries({ queryKey: ['project', pid] }).catch(() => {});
          }
        }
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [projectId, queryClient]);
}
